import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

/* =========================================================
   HORIZON GRID MEDIA FIX — NO CROPPING (FINAL)
   Fits 9:16 video inside 5:7 image box using contain
   ========================================================= */

const fixHorizonGridVideo = () => {
  document.querySelectorAll('media-gallery').forEach(gallery => {
    gallery.querySelectorAll('.product-media-container').forEach(container => {
      /* Lock gallery height to image ratio */
      container.style.aspectRatio = '5 / 7';
      container.classList.remove('product-media-container--tallest');

      /* Fix deferred media wrapper */
      container.querySelectorAll('deferred-media').forEach(dm => {
        dm.style.width = '100%';
        dm.style.height = '100%';
      });

      /* Fix poster button (main cropping culprit) */
      container.querySelectorAll('.deferred-media__poster-button').forEach(poster => {
        poster.style.width = '100%';
        poster.style.height = '100%';
        poster.style.aspectRatio = '5 / 7';
        poster.style.objectFit = 'contain';
      });

      /* Fix actual video / iframe */
      container.querySelectorAll('video, iframe').forEach(media => {
        media.style.width = '100%';
        media.style.height = '100%';
        media.style.objectFit = 'contain';
        media.style.backgroundColor = 'transparent';
      });
    });
  });
};

/* Run after media-gallery is ready */
customElements.whenDefined('media-gallery').then(() => {
  fixHorizonGridVideo();

  /* Re-run when Horizon re-renders (variants, resize, grid rebuild) */
  const observer = new MutationObserver(fixHorizonGridVideo);
  observer.observe(document.body, { childList: true, subtree: true });
});
